import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { startCheckout, verifyCheckout } from "../../api/checkout";
import { openPaystackCheckout } from "../../lib/paystack";
import { fetchMe } from "../../api/me";
import { useAsync } from "../../lib/useAsync";
import { ApiError } from "../../api/client";
import { UpgradeWallScreen } from "../../screens/payment/UpgradeWallScreen";
import type { PaymentStep } from "../../components/PaymentBusy";
import { PaymentStatusScreen } from "../../screens/payment/PaymentStatusScreen";

/**
 * The payment screens, connected.
 *
 * Every button here was inert — including "Pay ₦5,000 now" and both recovery
 * routes on the failed screen, which is the worst place in the product for a
 * dead end: a student who has just failed a payment and finds the retry button
 * does nothing concludes the whole thing is broken.
 *
 * Paystack redirection lands with the checkout integration; until then Pay goes
 * to the pending screen, which is the honest intermediate state anyway.
 */
export function UpgradeWallRoute() {
  const navigate = useNavigate();
  const [step, setStep] = useState<PaymentStep>(null);
  const me = useAsync(fetchMe);

  /*
   * The student's own scores, not two invented ones.
   *
   * The wall works precisely because it shows what they have already built —
   * showing someone else's numbers would undercut the only argument it makes.
   */
  const subjects = (me.data?.subjects ?? [])
    .filter((subject) => subject.readiness !== null)
    .slice(0, 2)
    .map((subject) => ({
      subject: subject.name,
      readiness: subject.readiness ?? 0,
      weakTopic: subject.weakTopics[0] ?? "",
    }));

  /**
   * Open Paystack's sheet, then have the charge confirmed.
   *
   * Three steps, and the order matters. The server fixes the reference and the
   * amount and writes a pending row; the sheet opens over the app so a student
   * can choose card, transfer or USSD without leaving; and what the sheet
   * reports is re-read from Paystack before anything unlocks.
   *
   * The browser's "it worked" is never taken as evidence. It names a reference,
   * and the backend asks Paystack what actually happened to it.
   */
  const pay = async () => {
    if (step) return;
    setStep("opening");

    try {
      const session = await startCheckout();

      openPaystackCheckout({
        reference: session.reference,
        email: session.email,
        amountMinor: session.amountMinor,
        // Paystack's sheet is up: the button is theirs to drive now.
        onLoad: () => setStep(null),
        onSuccess: (reference) => {
          // The sheet has closed; until the server confirms, say so on screen.
          setStep("confirming");
          void verifyCheckout(reference)
            .then((result) =>
              navigate(result.activated ? "/payment/success" : "/payment/pending", {
                state: { reference },
              }),
            )
            /*
             * The money may well have arrived; only our confirmation failed.
             * Pending, not failed — telling a student a successful charge
             * failed is how they end up paying twice.
             */
            .catch(() => navigate("/payment/pending", { state: { reference } }));
        },
        onCancel: () => setStep(null),
      });
    } catch (error) {
      setStep(null);
      // Nothing was charged: either the session could not be created or the
      // sheet could not open. That is exactly what the failed screen leads
      // with.
      void navigate("/payment/failed", {
        state: {
          reason:
            error instanceof ApiError
              ? error.message
              : error instanceof Error
                ? error.message
                : "We couldn't start that payment.",
        },
      });
    }
  };

  return (
    <UpgradeWallScreen
      step={step}
      subjects={subjects}
      onPay={() => void pay()}
      onAskParent={() => void navigate("/ask-parent")}
      // Home, not back: back is the question they were blocked on, which
      // would send them straight here again.
      onDismiss={() => void navigate("/home")}
    />
  );
}

export function PaymentSuccessRoute() {
  const navigate = useNavigate();
  return (
    <PaymentStatusScreen
      state="success"
      validUntil="August 2027"
      onContinue={() => void navigate("/practice")}
    />
  );
}

/**
 * Waiting on a charge that has not been confirmed yet.
 *
 * The student has already paid. The one thing this screen must not do is send
 * them back to pay again — which is what "I paid but nothing changed" did, by
 * navigating to the failed screen and its "Try again" button.
 *
 * So the primary action asks the gateway again. Only an answer that says the
 * charge is genuinely finished and unsuccessful moves them to the failed
 * screen; anything else keeps them here, because a transfer still clearing and
 * a declined card are different facts and only one of them means pay again.
 */
export function PaymentPendingRoute() {
  const navigate = useNavigate();
  const { reference } = (useLocation().state ?? {}) as { reference?: string };
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const checkAgain = async () => {
    if (!reference || checking) return;
    setChecking(true);
    setMessage(null);

    try {
      const result = await verifyCheckout(reference);

      if (result.status === "active") {
        void navigate("/payment/success", { replace: true });
        return;
      }

      if (result.status === "failed") {
        void navigate("/payment/failed", {
          replace: true,
          state: { reason: result.reason },
        });
        return;
      }

      setMessage(
        result.status === "review"
          ? "Your payment arrived but doesn't match what we expected, so someone is checking it by hand. Don't pay again — message support and we'll sort it."
          : "Still clearing. Bank transfers can take a few minutes — your money is safe and we'll unlock everything the moment it lands.",
      );
    } catch {
      setMessage("We couldn't reach the bank just now. Try again in a moment.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <PaymentStatusScreen
      state="pending"
      {...(reference ? { onCheckAgain: () => void checkAgain() } : {})}
      checking={checking}
      checkMessage={message}
      onContinue={() => void navigate("/practice")}
      onReportNotUnlocked={() => window.open("https://wa.me/2347000773724", "_blank", "noopener")}
    />
  );
}

export function PaymentFailedRoute() {
  const navigate = useNavigate();
  return (
    <PaymentStatusScreen
      state="failed"
      onRetry={() => void navigate("/upgrade")}
      onAskParent={() => void navigate("/ask-parent")}
      onSupport={() => window.open("https://wa.me/2347000773724", "_blank", "noopener")}
    />
  );
}
